import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Globe, Bot, Mail, Layers, ArrowRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, ErrorBox } from "../components/kit";
import { InboxMock } from "../components/marketing/Mocks";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <Logo />
        <div className="flex-1 flex items-center"><div className="w-full max-w-sm mx-auto py-10"><h1 className="text-2xl font-bold">{title}</h1>{sub && <p className="text-sm muted mt-1.5 mb-7">{sub}</p>}{children}</div></div>
      </div>
      <div className="hidden lg:flex bg-[#F6F8FC] border-l border-[#E2E8F0] items-center p-12 grid-bg"><div className="w-full"><InboxMock /></div></div>
    </div>
  );
}

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (e) => { e.preventDefault(); setBusy(true); setError(null); try { await fn(); } catch (x) { setError(errMsg(x)); } finally { setBusy(false); } };
  return { busy, error, run };
}

export function Login() {
  const [f, setF] = useState({ email: "", password: "" });
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const s = useSubmit(async () => { const { data } = await api.post("/auth/login", f); const me = await signIn(data.token); nav(me?.workspaces?.length ? sp.get("next") || "/app" : "/onboarding"); });
  return (
    <Shell title="Sign in to Nimbly" sub="Welcome back.">
      <form onSubmit={s.run} className="space-y-4" data-testid="login-form">
        <label className="block"><span className="label">Email</span><input className="input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" /></label>
        <label className="block"><span className="label flex justify-between">Password<Link to="/forgot-password" className="text-[#245BDB] font-normal" data-testid="forgot-link">Forgot?</Link></span><input className="input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" /></label>
        {s.error && <ErrorBox error={s.error} />}
        <button className="btn-primary w-full h-10" disabled={s.busy} data-testid="login-submit">{s.busy && <Spinner />}Sign in</button>
        <p className="text-sm muted text-center">No account? <Link to="/signup" className="text-[#245BDB]" data-testid="to-signup">Create one</Link></p>
      </form>
    </Shell>
  );
}

export function Signup() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: sp.get("email") || "", password: "" });
  const { signIn } = useAuth();
  const nav = useNavigate();
  const s = useSubmit(async () => { const { data } = await api.post("/auth/register", f); const me = await signIn(data.token); nav(me?.workspaces?.length ? "/app" : "/onboarding"); });
  return (
    <Shell title="Create your account" sub="Free to start. No credit card required.">
      <form onSubmit={s.run} className="space-y-4" data-testid="signup-form">
        <label className="block"><span className="label">Full name</span><input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="signup-name" /></label>
        <label className="block"><span className="label">Work email</span><input className="input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="signup-email" /></label>
        <label className="block"><span className="label">Password</span><input className="input" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="signup-password" /><span className="text-[11px] muted">At least 8 characters.</span></label>
        {s.error && <ErrorBox error={s.error} />}
        <button className="btn-primary w-full h-10" disabled={s.busy} data-testid="signup-submit">{s.busy && <Spinner />}Create account</button>
        <p className="text-sm muted text-center">Already have an account? <Link to="/login" className="text-[#245BDB]">Sign in</Link></p>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const s = useSubmit(async () => { await api.post("/auth/forgot-password", { email }); setDone(true); });
  return (
    <Shell title="Reset your password" sub="We'll email you a link to choose a new password.">
      {done ? <p className="text-sm rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800" data-testid="forgot-done">If an account exists for {email}, a reset link is on its way.</p> :
        <form onSubmit={s.run} className="space-y-4"><input className="input" type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />{s.error && <ErrorBox error={s.error} />}<button className="btn-primary w-full" disabled={s.busy} data-testid="forgot-submit">{s.busy && <Spinner />}Send reset link</button></form>}
      <Link to="/login" className="text-sm text-[#245BDB] block mt-6">Back to sign in</Link>
    </Shell>
  );
}

export function Reset() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const nav = useNavigate();
  const s = useSubmit(async () => { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Sign in with your new password."); nav("/login"); });
  return (
    <Shell title="Choose a new password">
      <form onSubmit={s.run} className="space-y-4"><input className="input" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" data-testid="reset-password" />{s.error && <ErrorBox error={s.error} />}<button className="btn-primary w-full" disabled={s.busy} data-testid="reset-submit">Update password</button></form>
    </Shell>
  );
}

const STEPS = ["Workspace", "Website", "Goal", "Next steps"];

export function Onboarding() {
  const { workspaces, refresh, setWsId, ws } = useAuth();
  const nav = useNavigate();
  const existing = ws && !ws.onboarding?.completed ? ws : null;
  const [step, setStep] = useState(existing ? Math.min(existing.onboarding?.step || 1, 3) : 0);
  const [name, setName] = useState("");
  const [site, setSite] = useState("");
  const [goal, setGoal] = useState("both");
  const [crawl, setCrawl] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = (st, extra = {}) => api.patch("/workspace/onboarding", { step: st, ...extra });

  const next = async () => {
    setBusy(true);
    try {
      if (step === 0) {
        const { data } = await api.post("/workspaces", { name, website: site });
        setWsId(data.id); await refresh(); await save(1);
      } else if (step === 1) {
        if (site.trim()) {
          await api.patch("/workspace", { website: site });
          if (crawl) { await api.post("/knowledge/sources", { type: "website", url: site }); toast.success("Crawl started — track it in Knowledge Base"); }
        }
        await save(2);
      } else if (step === 2) await save(3, { goal });
      setStep(step + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const finish = async (to) => { await save(4, { completed: true }); await refresh(); nav(to); };

  return (
    <div className="min-h-screen bg-[#F6F8FC]">
      <div className="h-16 border-b border-[#E2E8F0] bg-white px-6 flex items-center"><Logo />{workspaces.length > 0 && <Link to="/app" className="ml-auto text-sm muted" data-testid="onboarding-skip-all">Skip setup</Link>}</div>
      <div className="max-w-xl mx-auto py-14 px-5">
        <div className="flex gap-2 mb-8">{STEPS.map((s, i) => <div key={s} className="flex-1"><div className={`h-1 rounded-full ${i <= step ? "bg-[#245BDB]" : "bg-[#E2E8F0]"}`} /><div className={`text-[11px] mt-1.5 ${i === step ? "text-[#111827] font-medium" : "muted"}`}>{s}</div></div>)}</div>
        <div className="card p-7" data-testid={`onboarding-step-${step}`}>
          {step === 0 && <><h1 className="text-xl font-bold">Name your workspace</h1><p className="text-sm muted mt-1 mb-5">Usually your company or brand name.</p><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Inc." data-testid="onboarding-ws-name" /></>}
          {step === 1 && <><h1 className="text-xl font-bold flex items-center gap-2"><Globe size={18} />Your business website</h1><p className="text-sm muted mt-1 mb-5">Nimbly can crawl public pages to build your knowledge base. Optional.</p><input className="input" value={site} onChange={(e) => setSite(e.target.value)} placeholder="https://example.com" data-testid="onboarding-website" />
            <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" checked={crawl} onChange={(e) => setCrawl(e.target.checked)} data-testid="onboarding-crawl-toggle" />Start a crawl now (up to 25 pages)</label></>}
          {step === 2 && <><h1 className="text-xl font-bold">What do you want to do first?</h1><div className="grid gap-2 mt-5">{[["ai_support", Bot, "AI support", "Answer customer email with AI agents"], ["email_infra", Mail, "Email infrastructure", "Send transactional email via API"], ["both", Layers, "Both", "The full platform"]].map(([k, I, t, d]) => (
            <button key={k} onClick={() => setGoal(k)} className={`flex items-center gap-3 text-left rounded-md border p-3.5 transition-colors ${goal === k ? "border-[#245BDB] bg-[#F5F8FF]" : "border-[#E2E8F0] hover:bg-[#F6F8FC]"}`} data-testid={`onboarding-goal-${k}`}><I size={18} className="text-[#245BDB]" /><div><div className="text-sm font-medium">{t}</div><div className="text-xs muted">{d}</div></div>{goal === k && <Check size={16} className="ml-auto text-[#245BDB]" />}</button>))}</div></>}
          {step === 3 && <><h1 className="text-xl font-bold">You're set up</h1><p className="text-sm muted mt-1 mb-5">Pick up any of these now or later from the setup checklist on your dashboard.</p><div className="grid gap-2">{[["/app/agents/new", "Configure your AI agent"], ["/app/email/inbound", "Set up inbound email"], ["/app/knowledge", "Review your knowledge base"]].map(([to, t]) => <button key={to} onClick={() => finish(to)} className="flex items-center justify-between rounded-md border border-[#E2E8F0] p-3.5 text-sm font-medium hover:bg-[#F6F8FC]" data-testid={`onboarding-next-${to.split("/").pop()}`}>{t}<ArrowRight size={14} /></button>)}</div>
            <button className="btn-primary w-full mt-5" onClick={() => finish("/app")} data-testid="onboarding-finish">Go to dashboard</button></>}
          {step < 3 && <div className="flex justify-between mt-7">{step > 0 ? <button className="btn-ghost" onClick={() => { save(step + 1, { skipped: [STEPS[step]] }); setStep(step + 1); }} data-testid="onboarding-skip">Skip</button> : <span />}
            <button className="btn-primary" disabled={busy || (step === 0 && !name.trim())} onClick={next} data-testid="onboarding-continue">{busy && <Spinner />}Continue</button></div>}
        </div>
      </div>
    </div>
  );
}
